<script setup>
/**
 * FormControlPagesNavigation
 * Dynamic picklist selector controlling site tree menu navigation assignments.
 * Features an integrated custom text entry overwrite mechanism for adding group names,
 * with context-aware visibility inheritance linked directly to parent elements.
 */
import {ref, computed, watch, onMounted, onBeforeUnmount, nextTick} from 'vue'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'
import {useSiteTreeStore} from '@/stores/siteTree'
import Button from '@/common/Button.vue'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    }
})

// Pinia Stores
const itemDataStore = useItemDataStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)
const siteTreeStore = useSiteTreeStore('siteTree')

// --- State Layers ---

const other = ref(false)
const otherValue = ref('')

// Element DOM references
const inputRef = ref(null)
const dropdownButtonRef = ref(null)

// Timer tracker placeholder
let navigationReloadTimer = null

formStore.value?.setDefaultValue({
    name: props.field.name,
    value: ''
})

// --- Computed ---

const dropdownId = computed(() => `dropdownMenuField${props.field?.id || ''}`)
const parent = computed(() => props.currentData?.parent)
const navigationGroups = computed(() => siteTreeStore.navigationGroups || [])
const formSmall = computed(() => formStore.value?.formSmall || false)

// --- Methods ---

/**
 * Triggers background checks to reload updated navigation taxonomy trees.
 * Uses explicit garbage collection tracking to prevent memory leaks.
 */
const reloadNavigationGroups = () => {
    if (navigationReloadTimer) clearTimeout(navigationReloadTimer)

    navigationReloadTimer = setTimeout(() => {
        siteTreeStore.setNavigationGroups(true)
    }, 1000)
}

/**
 * Contextual Visibility Manager
 * Evaluates node structures; if a subpage exists, visibility drops,
 * and it inherits its primary menu route parameters directly from its parent.
 */
const toggleFieldVisibility = async () => {
    if (!props.field || !props.currentData) return

    const activeParentId = parent.value
    props.field.visibility = activeParentId === 0 ? '' : 'hidden'

    if (activeParentId > 0) {
        try {
            const parentPageData = await itemDataStore.get({
                tableName: 'pages',
                id: activeParentId
            })
            if (parentPageData?.navigation) {
                props.currentData[props.field.name] = parentPageData.navigation
            }
        } catch (err) {
            console.error('Failed to resolve structural parent page navigation metadata:', err)
        }
    }
}

const setValue = (groupName) => {
    if (props.currentData && props.field?.name) {
        props.currentData[props.field.name] = groupName
        reloadNavigationGroups()
    }
}

const showOtherForm = () => {
    other.value = true
    nextTick(() => {
        inputRef.value?.select()
    })
}

const hideOtherForm = () => {
    other.value = false
    otherValue.value = ''

    nextTick(() => {
        // Safe access: Queries native focus controls via direct template assignment references
        if (dropdownButtonRef.value) {
            const rawElement = dropdownButtonRef.value.$el || dropdownButtonRef.value
            rawElement?.focus()
        }
    })
}

const applyOtherValue = () => {
    if (otherValue.value && props.field?.name) {
        props.currentData[props.field.name] = otherValue.value
        hideOtherForm()
        reloadNavigationGroups()
    }
}

// --- Structural Watchers & Lifecycles ---

watch(parent, async () => {
    await toggleFieldVisibility()
})

onMounted(async () => {
    await toggleFieldVisibility()
    await siteTreeStore.setNavigationGroups()
})

onBeforeUnmount(() => {
    // Prevent background state mutations on dead component trees
    if (navigationReloadTimer) {
        clearTimeout(navigationReloadTimer)
    }
})
</script>

<template>
    <div class="form-control-pages-navigation-container">

        <div v-if="other" class="d-flex align-items-center">
            <div class="input-group">
                <input
                    ref="inputRef"
                    v-model="otherValue"
                    type="text"
                    class="form-control"
                    :class="{'form-control-sm': formSmall, 'is-invalid': field.error}"
                    placeholder="Submit your new value"
                    @keyup.esc="hideOtherForm"
                    @keyup.enter="applyOtherValue"
                >

                <Button
                    :default-class="'btn-primary'"
                    :class="{'btn-sm': formSmall, 'btn-warning': otherValue.length > 0}"
                    @click="applyOtherValue"
                >
                    <i class="bi-arrow-return-left"/>
                </Button>
            </div>

            <Button
                :default-class="'btn-link'"
                :class="{'btn-sm': formSmall}"
                @click="hideOtherForm"
            >
                <i class="bi-x-lg"/>
            </Button>
        </div>

        <div v-show="!other" class="dropdown">
            <Button
                ref="dropdownButtonRef"
                :id="dropdownId"
                aria-expanded="true"
                class="dropdown-toggle border"
                :class="{'btn-sm': formSmall}"
                data-bs-toggle="dropdown"
            >
                {{ currentData[field.name] || 'Select group…' }}
            </Button>

            <ul :aria-labelledby="dropdownId" class="dropdown-menu">
                <li v-for="group in navigationGroups" :key="group">
                    <span
                        role="button"
                        class="dropdown-item"
                        :class="{ 'active': group === currentData[field.name], 'small': formSmall }"
                        @click="setValue(group)"
                    >
                        {{ group }}
                    </span>
                </li>

                <li>
                    <hr class="dropdown-divider">
                </li>

                <li>
                    <span
                        class="dropdown-item"
                        :class="{'small': formSmall}"
                        @click="showOtherForm"
                        role="button"
                    >
                        Other&hellip;
                    </span>
                </li>
            </ul>
        </div>

    </div>
</template>